import React, { useMemo } from 'react';
import { Ionicons } from '@expo/vector-icons';
import { AccessPoint, KIND_ICONS, KIND_LABELS, Summary, ago } from './sentinel';

type Props = {
  points: AccessPoint[];
  summary?: Summary;
  selected?: string;
  // uuid del punto → { at, denied } del último intento en vivo (dispara el pulso)
  pulses: Record<string, { at: number; denied: boolean }>;
  now: number;
  onSelect: (uuid: string | undefined) => void;
};

/** Zonas y sus puntos de acceso, con la actividad del periodo y un pulso al llegar algo en vivo. */
export default function ZoneRail({ points, summary, selected, pulses, now, onSelect }: Props) {
  const stats = useMemo(
    () => Object.fromEntries((summary?.access_points ?? []).map(s => [s.access_point, s])),
    [summary],
  );

  const zones = useMemo(() => {
    const groups = new Map<string, { name: string; points: AccessPoint[] }>();
    points.forEach(p => {
      const key = p.zone?.uuid ?? 'none';
      if (!groups.has(key)) groups.set(key, { name: p.zone?.name ?? 'Sin zona', points: [] });
      groups.get(key)!.points.push(p);
    });
    return [...groups.entries()].sort(([a], [b]) => (a === 'none' ? 1 : b === 'none' ? -1 : 0));
  }, [points]);

  return (
    <div className="cc-col cc-rail">
      <div className="cc-col-head">
        <Ionicons name="git-network-outline" size={14} color="currentColor" />
        Puntos de acceso
      </div>
      <div className="cc-scroll">
        {zones.length === 0 && (
          <div className="cc-empty" style={{ padding: '30px 16px', fontSize: 12.5 }}>
            Esta comunidad todavía no tiene puntos de acceso.
          </div>
        )}
        {zones.map(([key, zone]) => {
          const total = zone.points.reduce((n, p) => n + (stats[p.uuid]?.total ?? 0), 0);
          return (
            <div className="cc-zone" key={key}>
              <div className="cc-zone-name">
                <span>{zone.name}</span>
                <span className="cc-num">{total}</span>
              </div>
              {zone.points.map(point => {
                const s = stats[point.uuid];
                const pulse = pulses[point.uuid];
                const on = selected === point.uuid;
                return (
                  <button
                    key={point.uuid}
                    className={`cc-point${on ? ' on' : ''}`}
                    onClick={() => onSelect(on ? undefined : point.uuid)}
                    title={on ? 'Quitar filtro' : `Ver solo ${point.name}`}
                  >
                    <span className="cc-point-icon">
                      <Ionicons name={KIND_ICONS[point.kind] as any} size={17} color="currentColor" />
                      {pulse && <span key={pulse.at} className={`cc-radar fire${pulse.denied ? ' denied' : ''}`} />}
                    </span>
                    <span style={{ minWidth: 0 }}>
                      <div className="cc-point-name">{point.name}</div>
                      <div className="cc-point-meta">
                        {KIND_LABELS[point.kind]}
                        {point.direction !== 'both' && ` · ${point.direction === 'entry' ? 'entrada' : 'salida'}`}
                        {s?.last_at && ` · ${ago(s.last_at, now)}`}
                      </div>
                    </span>
                    <span className="cc-point-count cc-num">
                      {s?.total ?? 0}
                      {!!s?.denied && <small>{s.denied} negado{s.denied === 1 ? '' : 's'}</small>}
                    </span>
                  </button>
                );
              })}
            </div>
          );
        })}
      </div>
    </div>
  );
}
